import React, { Suspense, useContext, useEffect, useState } from 'react';
import { LoginContext } from 'src/lib/LoginContext';
import { ScrollContext } from 'src/lib/ScrollContext';
import { IconClose, IconMenu, IconWarning, IconChevronDown, Typography, cn, IconChevronUp, TextLink } from '@up-riac/bespoke-ui';
import { SkipLink } from 'src/components/SkipLink/SkipLink';
import RIACGoldLogo from '@riac/core/assets/riac_logo_transparent_gold_full.png?h=95';
import { SocketContext } from 'src/lib/SocketContext';
import { GlobalBannerContainer } from '../GlobalBannerContainer';
import { HeaderIconButton } from './components/HeaderIconButton';
import { OverlayContainer } from '@react-aria/overlays';
import { UtilityHeader } from './components/UtilityHeader';
import { MegaMenu } from './components/MegaMenu/MegaMenu';
import { useFetch } from 'src/lib/FetchContext';
import { GlobalDataContext } from 'src/lib/GlobalDataContext';
import { MegaMenuAuctionsPage } from './components/MegaMenu/MegaMenuAuctionsPage';
import { MegaMenuConsignmentPage } from './components/MegaMenu/MegaMenuConsignmentPage';
import { MegaMenuLatestPage } from './components/MegaMenu/MegaMenuLatestPage';
import { MegaMenuCompanyPage } from './components/MegaMenu/MegaMenuCompanyPage';
import { MegaMenuAccountPage } from './components/MegaMenu/MegaMenuAccountPage';
import { HeaderAccountSection } from 'src/components/Header/components/HeaderAccountSection/HeaderAccountSection';
import { HeaderMobileMenu } from 'src/components/Header/components/HeaderMobileMenu/HeaderMobileMenu';
import { FocusScope } from '@react-aria/focus';
import { AnalyticsContext } from 'src/lib/AnalyticsContext';
import { Banner, Button, Grid2, Modal, useScrollControl } from '@riac/design-system';
import { MegaMenuContext } from 'src/components/Header/components/MegaMenu/useMegaMenu';
import { useHeaderContext } from 'src/components/Header/components/HeaderContext';

const NavigationLink = ({
  label,
  to,
  disabled,
  className,
  onPress,
  isOpen,
}: {
  label: string,
  disabled?: boolean,
  to?: string,
  className?: string,
  onPress?: () => void,
  isOpen?: boolean,
}): React.ReactElement => {
  return (
    <Button
      to={disabled ? '/login' : to}
      variant='link'
      className={cn(
        'flex flex-row gap-px text-text-contrast',
        'no-underline decoration-2 hover:text-text active:text-text decoration-text p-0',
        isOpen && 'underline text-text hover:underline decoration-accent',
        disabled && 'opacity-50', className
      )}
      onPress={onPress}
      id={`header-navigation-link-${label}`}
      data-dont-close-mega-menu={'true'}
    >
      <Typography weight='semibold' className='leading-5 block' data-dont-close-mega-menu={'true'}>{label}</Typography>
      {!isOpen && <IconChevronDown className='size-6 mt-1' data-dont-close-mega-menu={'true'}/>}
      {isOpen && <IconChevronUp className='size-6 mt-1' data-dont-close-mega-menu={'true'}/>}
    </Button>
  );
};

export const Header = (): React.ReactElement => {
  const { isLoggedIn } = useContext(LoginContext);
  const { headerRef } = useContext(ScrollContext);
  const {
    showIcon, showModal: showDisconnectionModal, setShowModal: setShowDisconnectionModal,
  } = useContext(SocketContext);
  const [
    currentMegaMenuPath, setCurrentMegaMenuPath,
  ] = useState<'auctions' | 'consignment' | 'latest' | 'company' | 'account' | null>(null);
  const {
    megaMenuState,
    accountMenuState,
    mobileMenuState,
    settingsMenuState,
    setMobileMenuPage,
  } = useHeaderContext();
  const mobileMenuButtonRef = React.useRef<HTMLButtonElement>(null);

  const { sendHotjarEvent } = useContext(AnalyticsContext);
  const { fetch } = useFetch();
  const [upcomingAuctions, setUpcomingAuctions] = useState<any[]>([]);
  const [currentAuctions, setCurrentAuctions] = useState<any[]>([]);
  const [pastAuctions, setPastAuctions] = useState<any[]>([]);
  const [latestFeaturedPost, setLatestFeaturedPost] = useState<any>(null);

  // Refresh upcoming and past auctions every 15 minutes
  // active auctions and current auctions are refreshed via global data context
  // This is done separately because it is only used when the mega menu is open - so we can skip it on SSR/initial load
  // and we don't want to block the header from loading while we fetch this data
  const updateStaleData = async (): Promise<void> => {
    const mmDataRequest = await fetch('/api/mega-menu/data');
    if(!mmDataRequest.ok) {
      setUpcomingAuctions([]);
      setCurrentAuctions([]);
      setPastAuctions([]);
      setLatestFeaturedPost(null);
      console.error('Failed to fetch mega menu data');
      return;
    }
    const mmData = await mmDataRequest.json();
    if(!mmData.success) {
      setUpcomingAuctions([]);
      setCurrentAuctions([]);
      setPastAuctions([]);
      setLatestFeaturedPost(null);
      console.error('No mega menu data found.');
      return;
    }
    setUpcomingAuctions(mmData.data.upcomingAuctions);
    setCurrentAuctions(mmData.data.currentAuctions);
    setPastAuctions(mmData.data.pastAuctions);
    if(mmData.data.recentPosts && mmData.data.recentPosts.length > 0) {
      setLatestFeaturedPost(mmData.data.recentPosts[0]);
    } else {
      setLatestFeaturedPost(null);
    }
  };

  // Use a 15 minute timer to check for updates, but only update if the tab is visible and active.
  useEffect(() => {
    updateStaleData();

    const FIFTEEN_MINUTES = 1000 * 60 * 15;
    let intervalId: ReturnType<typeof setInterval>;

    const startInterval = (): void => {
      clearInterval(intervalId);
      intervalId = setInterval(() => {
        if(!document.hidden) {
          updateStaleData();
        }
      }, FIFTEEN_MINUTES);
    };

    const onVisibilityChange = (): void => {
      if(!document.hidden) {
        updateStaleData();
        startInterval();
      }
    };

    startInterval();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // three part effect:
  // Close mega menu and account menu when screen shrinks to mobile
  // Close mobile menu when screen grows to desktop
  // Enable scroll lock for mobile menu on small screens
  // Disable scroll lock for mega menu on large screens
  const [preventScrollDueToWindowSize, setPreventScrollDueToWindowSize] = useState(false);
  useEffect(() => {
    const onResize = (): void => {
      if(window.innerWidth < 1024) {
        setPreventScrollDueToWindowSize(true);
        megaMenuState.close();
        accountMenuState.close();
      } else {
        setPreventScrollDueToWindowSize(false);
        mobileMenuState.close();
      }
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [mobileMenuState.isOpen]);
  useScrollControl(preventScrollDueToWindowSize && mobileMenuState.isOpen);

  // This effect is used to update the header padding when the document's padding-right changes.
  // This can happen when the scrollbar appears or disappears
  // E.G. when the user opens a picker component.
  useEffect(() => {
    const documentElement = document.documentElement;
    let currentPadding = '';

    const observer = new MutationObserver((mutationsList) => {
      for(const mutation of mutationsList) {
        if(mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const newPadding = documentElement.style.paddingRight;
          if(newPadding !== currentPadding && headerRef.current) {
            headerRef.current.style.paddingRight = newPadding;
            currentPadding = newPadding;
          }
        }
      }
    });

    observer.observe(documentElement, { attributes: true });

    const initialPadding = documentElement.style.paddingRight;
    if(initialPadding && headerRef.current) {
      headerRef.current.style.paddingRight = initialPadding;
      currentPadding = initialPadding;
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const isAnyMenuOpen = megaMenuState.isOpen
    || accountMenuState.isOpen
    || mobileMenuState.isOpen
    || settingsMenuState.isOpen;

  const headerContent = (
    <>
      <div
        ref={headerRef}
        className={cn(
          'transition-all sticky dark backdrop-blur-xs',
          'top-0 z-40 w-full bg-surface-dark/80 border-b border-b-accent lg:border-b-transparent print:hidden',
          'hover:bg-surface-dark',
          'focus-within:bg-surface-dark hover:border-b-accent lg:hover:border-b-accent focus-within:border-b-accent',
          isAnyMenuOpen && 'bg-surface-dark border-b-accent lg:border-b-accent',
        )}
      >
        <Grid2
          innerContainerClassName='h-[30px] dark w-full grid grid-cols-[1fr_auto_1fr] items-center gap-x-4'
          className={cn(
            'py-0',
          )}
        >
          <div className='col-start-3 justify-self-end flex-row items-center h-[22px] hidden md:flex'>
            <div className='pointer-events-auto'>
              <TextLink color='text' id='utility-faq-link' to='/faq' UNSAFE_className='block p-0 px-2'>
                <Typography size='sm' weight='semibold'>FAQ</Typography>
              </TextLink>
            </div>
            <div className='w-px h-[15px] bg-text-contrast'/>
            <div className='pointer-events-auto'>
              <TextLink color='text' id='utility-contact-link' to='/contact' UNSAFE_className='block p-0 px-2'>
                <Typography size='sm' weight='semibold'>Contact</Typography>
              </TextLink>
            </div>
          </div>
        </Grid2>
        <Grid2 className={cn(
          'pb-3 pt-0',
        )} innerContainerClassName='relative'>
          <div className={'flex flex-col w-full'}>
            <div className={'flex items-center'}>
              <SkipLink data-allow-press-while-closing-mega-menu='true'/>
              <Button
                data-allow-press-while-closing-mega-menu='true'
                to='/'
                className={cn('p-0 h-16 flex flex-row gap-8 items-center shrink-0 mr-14')}
                unstyled
              >
                <img src={RIACGoldLogo} alt='Rock Island Auction Company' className={cn('h-16 w-[112px]')}/>
              </Button>
              {showIcon
                && <Button
                  data-allow-press-while-closing-mega-menu='true'
                  aria-label='Alert'
                  unstyled
                  className={'text-white bg-error p-2 ml-5'}
                  onPress={() => setShowDisconnectionModal(true)}
                >
                  <IconWarning/>
                </Button>
              }
              <div className='flex-row gap-4 hidden lg:flex' data-allow-press-while-closing-account-popover='true'>
                <NavigationLink onPress={() => {
                  setCurrentMegaMenuPath('auctions');
                  sendHotjarEvent('UX-mega_menu_opened');
                  sendHotjarEvent('UX-mega_menu_auctions_opened');
                  if(currentMegaMenuPath === 'auctions' && megaMenuState.isOpen) {
                    megaMenuState.close();
                  } else {
                    megaMenuState.open();
                  }
                }} label='Auctions' isOpen={currentMegaMenuPath === 'auctions' && megaMenuState.isOpen} />
                <NavigationLink onPress={() => {
                  setCurrentMegaMenuPath('consignment');
                  sendHotjarEvent('UX-mega_menu_opened');
                  sendHotjarEvent('UX-mega_menu_consignment_opened');
                  if(currentMegaMenuPath === 'consignment' && megaMenuState.isOpen) {
                    megaMenuState.close();
                  } else {
                    megaMenuState.open();
                  }
                }} label='Consignment' isOpen={currentMegaMenuPath === 'consignment' && megaMenuState.isOpen} />
                <NavigationLink onPress={() => {
                  setCurrentMegaMenuPath('latest');
                  sendHotjarEvent('UX-mega_menu_opened');
                  sendHotjarEvent('UX-mega_menu_latest_opened');
                  if(currentMegaMenuPath === 'latest' && megaMenuState.isOpen) {
                    megaMenuState.close();
                  } else {
                    megaMenuState.open();
                  }
                }} label='Latest' isOpen={currentMegaMenuPath === 'latest' && megaMenuState.isOpen} />
                <NavigationLink onPress={() => {
                  setCurrentMegaMenuPath('company');
                  sendHotjarEvent('UX-mega_menu_opened');
                  sendHotjarEvent('UX-mega_menu_company_opened');
                  if(currentMegaMenuPath === 'company' && megaMenuState.isOpen) {
                    megaMenuState.close();
                  } else {
                    megaMenuState.open();
                  }
                }} label='Company' isOpen={currentMegaMenuPath === 'company' && megaMenuState.isOpen} />
                {isLoggedIn && <NavigationLink onPress={() => {
                  setCurrentMegaMenuPath('account');
                  sendHotjarEvent('UX-mega_menu_opened');
                  sendHotjarEvent('UX-mega_menu_account_opened');
                  if(currentMegaMenuPath === 'account' && megaMenuState.isOpen) {
                    megaMenuState.close();
                  } else {
                    megaMenuState.open();
                  }
                }} label='Account' isOpen={currentMegaMenuPath === 'account' && megaMenuState.isOpen}/>}
              </div>
              <div className='flex grow' />
              <div className='flex flex-row items-center self-center justify-end gap-5 h-min'>
                <div className='flex flex-row items-center gap-3' data-allow-press-while-closing-mega-menu='true'>
                  <HeaderAccountSection accountMenuState={accountMenuState} />
                  <HeaderIconButton
                    label='Menu'
                    IconComponent={mobileMenuState.isOpen ? IconClose : IconMenu}
                    ref={mobileMenuButtonRef}
                    onPress={() => {
                      mobileMenuState.toggle();
                      setMobileMenuPage('main');
                    }}
                    className='lg:hidden'
                    id='header-mobile-menu-button'
                  />
                </div>
              </div>
            </div>
          </div>
        </Grid2>
        <div className='relative'>
          {megaMenuState.isOpen
            && <MegaMenuContext.Provider value={{ megaMenuState, settingsMenuState }}>
              <MegaMenu onClose={() => megaMenuState.close()} isOpen={megaMenuState.isOpen}>
                {currentMegaMenuPath === 'auctions' && <MegaMenuAuctionsPage
                  currentAuctions={currentAuctions} upcomingAuctions={upcomingAuctions} pastAuctions={pastAuctions}
                />}
                {currentMegaMenuPath === 'consignment' && <MegaMenuConsignmentPage/>}
                {currentMegaMenuPath === 'latest' && <MegaMenuLatestPage latestFeaturedPost={latestFeaturedPost}/>}
                {currentMegaMenuPath === 'company' && <MegaMenuCompanyPage/>}
                {currentMegaMenuPath === 'account' && <MegaMenuAccountPage/>}
              </MegaMenu>
            </MegaMenuContext.Provider>
          }
        </div>
        <div className='relative'>
          {mobileMenuState.isOpen
            && <HeaderMobileMenu
              currentAuctions={currentAuctions}
              upcomingAuctions={upcomingAuctions}
              pastAuctions={pastAuctions}
              state={mobileMenuState} />
          }
        </div>
      </div>
      <GlobalBannerContainer />
      <Suspense>
        <OverlayContainer>
          <Modal
            title={'Disconnected'}
            isOpen={showDisconnectionModal}
            onClose={() => {
              setShowDisconnectionModal(false);
            }}
            isDismissable
            showCloseButton
            aria-labelledby={'disconnection-modal'}
            footerButtons={<>
              <Button onPress={() => setShowDisconnectionModal(false)}>
                Close
              </Button>
            </>}
          >
            <Banner color='error' size='sm'>
              <div className='flex flex-col gap-2'>
                <div>
                  Unable to connect to live update server, attempting reconnection.
                  If this persists, please try refreshing your browser.
                </div>
                <div>
                  Disconnected state indicated by <IconWarning/> at top of page.
                  If you cannot see this icon, you are connected.
                </div>
              </div>
            </Banner>
          </Modal>
        </OverlayContainer>
      </Suspense>
      <Button unstyled className='absolute' id='maincontent' aria-label='Main content' />
    </>
  );

  return <>
    <UtilityHeader data-allow-press-while-closing-mega-menu='true'/>
    <div className='z-[9999] absolute left-px top-px hidden md:block text-red-500 font-medium text-xs'>
      {VISIBLE_VERSION_HASH === 'true' && <>v{VERSION_HASH.substring(0, 6)}</> || ''}
    </div>
    {(megaMenuState.isOpen && !settingsMenuState.isOpen) ? (
      <FocusScope contain restoreFocus autoFocus>
        {headerContent}
      </FocusScope>
    ) : (
      headerContent
    )}
  </>;
};
